// ============================================================
// Chess Arena — Core Type Definitions
// ============================================================

// --- Board & Grid ---

export const BOARD_COLS = 5;
export const BOARD_ROWS = 7;

export interface GridPos {
  col: number;
  row: number;
}

// --- Pieces ---

export type PieceType = 'King' | 'Pawn' | 'Knight' | 'Bishop' | 'Rook' | 'Queen';
export type Team = 'player' | 'enemy';

export interface PieceConfig {
  type: PieceType;
  cooldown: number;   // seconds
  value: number;
  introducedAtLevel: number;
}

export const PIECE_CONFIGS: Record<PieceType, PieceConfig> = {
  King:   { type: 'King',   cooldown: 2.2, value: 100, introducedAtLevel: 1 },
  Pawn:   { type: 'Pawn',   cooldown: 2.2, value: 1,   introducedAtLevel: 1 },
  Knight: { type: 'Knight', cooldown: 2.2, value: 3,   introducedAtLevel: 1 },
  Bishop: { type: 'Bishop', cooldown: 2.2, value: 3,   introducedAtLevel: 3 },
  Rook:   { type: 'Rook',   cooldown: 2.2, value: 5,   introducedAtLevel: 5 },
  Queen:  { type: 'Queen',  cooldown: 2.2, value: 9,   introducedAtLevel: 9 },
};

export interface PieceState {
  id: string;
  type: PieceType;
  team: Team;
  pos: GridPos;
  cooldownRemaining: number;
  isMoving: boolean;
  moveStartTime: number;
  moveTarget: GridPos | null;
  isInBush: boolean;
  premove: GridPos | null;
  isAlive: boolean;
  isFrozen: boolean;
  frozenUntil: number;       // elapsed game time in seconds
  isShielded: boolean;
  shieldedUntil: number;     // elapsed game time in seconds
}

// --- Terrain ---

export type TerrainType = 'rock' | 'bush';

export interface TerrainTile {
  type: TerrainType;
  pos: GridPos;
}

export interface TerrainTypeConfig {
  spriteKey: string;
  variants: string[];
  passable: boolean;
  blocksLineOfSight: boolean;
  grantsStealth: boolean;
  hiddenOverlay?: string;
  animation: string | null;
}

// --- Spawning ---

export type SpawnWeights = Partial<Record<PieceType, number>>;

export interface SpawnConfig {
  timerSeconds: number;
  weights: SpawnWeights;
}

// --- Waves ---

export interface WaveConfig {
  triggerTimeSec: number;
  pieces: Partial<Record<PieceType, number>>;
}

// --- Level Config ---

export interface OpponentConfig {
  name: string;
  rating: number;
  portrait: string;
}

export interface IntroTip {
  spotlightTarget: string;
  text: string;
}

export interface LevelConfig {
  level: number;
  opponent: OpponentConfig;
  playerArmy: Partial<Record<PieceType, number>>;
  enemyArmy: Partial<Record<PieceType, number>>;
  enemyKingVisible: boolean;
  enemyKingAppearsAfterWave: number | null;
  playerSpawn: SpawnConfig;
  enemySpawn: SpawnConfig;
  waves: WaveConfig[];
  terrain: {
    maxRocks: number;
    maxBushes: number;
  };
  matchTimerSec: number | null;
  armyCap?: number;                 // per-level override of GameSettings.armyCap
  introTips: IntroTip[];
  tutorialLevel: boolean;
  newMechanic: string | null;
  /** AI difficulty overrides — merged with preset. */
  aiDifficulty?: {
    /** Preset name: 'easy' | 'medium' | 'hard' | 'brutal' */
    preset?: string;
    /** Override individual params (merged on top of preset). */
    depth?: number;
    evalNoise?: number;
    blunderRate?: number;
    moveInterval?: number;
    movesPerTick?: number;
  };
}

// --- Tutorial ---

export interface TutorialStep {
  stepId: string;
  spotlightTarget: string;
  text: string;
  requiredAction: string | null;
  enemyState: 'frozen' | 'active';
  highlightElement: string;
  aiDialogue: string | null;
  /** When true, cooldowns are paused during this action-gated step. */
  pauseCooldowns?: boolean;
  /** When true, the player cannot interact with any piece during this step. */
  blockInteraction?: boolean;
  /** Grant an ability to the player during this tutorial step. */
  grantAbility?: string;
  /** Script a specific piece to move to a destination (used instead of AI). */
  scriptedMove?: {
    /** Piece identifier — e.g. "enemyKing", "enemyPawn" */
    piece: string;
    /** Grid destination */
    destination: GridPos;
  };
}

export interface TutorialDialogue {
  trigger: string;
  text: string;
}

export interface TutorialLevelConfig {
  steps: TutorialStep[];
  aiDialogue?: TutorialDialogue[];
}

// --- Progression ---

export interface TrophyBracket {
  minTrophies: number;
  maxTrophies: number;
  winGain: number;
  lossLoss: number;
}

export interface ArenaConfig {
  id: number;
  trophies: number;
  name: string;
  boardTheme: string;
  unlockRewardCoins: number;
}

export interface XPLevelConfig {
  level: number;
  xpRequired: number;
}

// --- Daily Tasks ---

export interface TaskConfig {
  taskId: string;
  description: string;
  difficulty: 'easy' | 'medium' | 'hard';
  trackingEvent: string;
  trackingFilter: Record<string, string> | null;
  target: number;
  reward: { coins: number; xp: number };
  requiresMinLevel: number;
  requiresMechanic: string | null;
  weight: number;
}

export interface ActiveTask {
  config: TaskConfig;
  progress: number;
  claimed: boolean;
}

// --- Chests ---

export type ChestTier = 'bronze' | 'silver' | 'gold' | 'royal';

export type ChestSlotState = 'empty' | 'locked' | 'unlocking' | 'ready';

export interface ChestSlot {
  state: ChestSlotState;
  tier: ChestTier | null;
  unlockEndTime: number | null;  // timestamp ms
}

export interface ChestRewardRange {
  coins: [number, number];
  xp: [number, number];
  abilityCharges: Record<string, [number, number]>;
}

// --- Tools & Abilities ---

export type AbilityTargeting = 'cell' | 'enemy_piece' | 'global' | 'auto';
export type AbilityRarity = 'common' | 'rare' | 'epic';

export interface ToolConfigEntry {
  displayName: string;
  description: string;
  iconKey: string;
  goldCost: number;
  usesPerMatch: number;
  unlocksAtLevel: number;
}

export interface AbilityConfigEntry {
  displayName: string;
  description: string;
  rarity: AbilityRarity;
  iconKey: string;
  targeting: AbilityTargeting;
  effectRadius: number | null;
  effectDurationSec: number | null;
  tutorialGrant: boolean;
  tutorialGrantLevel: number | null;
  shardsToUnlock: number | null;
  startingCharges: number;
  maxCharges: number;
  excessShardsPerCharge: number;
  usesPerMatch: number;
  cooldownSec: number;
  arenaUnlock?: number;
}

// --- Player State ---

export interface PlayerStateData {
  currentLevel: number;
  highestLevelCompleted: number;
  trophies: number;
  coins: number;
  xp: number;
  xpLevel: number;
  lives: number;
  maxLives: number;
  lastLifeRegenTimestamp: number;
  winStreak: number;
  chestSlots: ChestSlot[];
  overflowChest: ChestSlot | null;
  dailyTasks: ActiveTask[];
  dailyTasksLastReset: string; // ISO date string
  dailyChestClaimed: boolean;
  bronzeStreak: number;        // pity counter
  chestsSinceGold: number;     // pity counter
  abilitiesUnlocked: string[];
  abilityShards: Record<string, number>;
  abilityCharges: Record<string, number>;
  equippedAbilities: string[];
  onboardingComplete: boolean;
  mainMenuTutorialDone: boolean;
  saveVersion: number;
}

// --- Events ---

export type GameEventType =
  | 'match_played'
  | 'match_won'
  | 'match_lost'
  | 'piece_captured'
  | 'piece_moved'
  | 'piece_spawned'
  | 'premove_queued'
  | 'ability_used'
  | 'tool_used'
  | 'trophies_earned'
  | 'win_streak';

export interface GameEvent {
  type: GameEventType;
  data?: Record<string, unknown>;
}

// --- Game Settings ---

export interface GameSettingsConfig {
  board: {
    cols: number;
    rows: number;
    cellSize: number;
    slidingSpeed: number;        // cells per second
    knightJumpDuration: number;  // seconds
  };
  cooldowns: Record<PieceType, number>;
  armyCap: number;                  // max alive pieces per side
  dragThreshold: number;          // pixels
  cooldownDimAlpha: number;
  bushAlphaOwn: number;
  premoveEnabled: boolean;
  premoveDisabledUntilLevel: number;
}

// --- Asset Manifest ---

export interface AssetEntry {
  key: string;
  path: string;
  type: 'image' | 'spritesheet' | 'atlas' | 'audio' | 'json';
  frameWidth?: number;
  frameHeight?: number;
  description: string;
  futureUpgrade?: string;  // notes for when real art arrives
}

// --- Match Result ---

export type MatchResult = 'victory' | 'defeat';

export interface MatchRewards {
  coins: number;
  xp: number;
  trophyChange: number;
  chestTier: ChestTier | null;
}
